/**
 * Utility functions for PostHog filtering logic
 *
 * Detection Methods:
 * 1. localStorage flag: 'posthog_internal' (set via /tracking page)
 *
 * Note: Internal users must manually enable tracking exclusion via the /tracking page.
 * This ensures privacy and security (no emails exposed in client code).
 * The localStorage flag persists through logout/login cycles.
 */

/**
 * Check if hostname is localhost or a development environment
 * Only blocks:
 * - Explicit localhost patterns (localhost, 127.0.0.1, etc.)
 * - Private IPs on common development ports (5173, 3000, 8080, etc.)
 * - .local domains
 *
 * Note: We don't block all private IPs because they can appear in production
 * (VPN users, corporate networks). We only block them when on dev ports.
 */
export function isLocalhost(hostname: string): boolean {
  if (!hostname) return false

  const lowerHostname = hostname.toLowerCase()

  // Check for common localhost patterns
  if (
    lowerHostname === 'localhost' ||
    lowerHostname === '127.0.0.1' ||
    lowerHostname === '[::1]' ||
    lowerHostname.endsWith('.local') ||
    lowerHostname.endsWith('.localhost')
  ) {
    return true
  }

  // Check if it's a private IP (RFC 1918)
  // Only block if combined with a common development port
  const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/
  const match = hostname.match(ipPattern)

  if (match && typeof window !== 'undefined') {
    const [, octet1, octet2] = match.map(Number)
    const port = window.location.port

    // Common development server ports
    const devPorts = ['5173', '3000', '8080', '8081', '4200', '5000', '8000', '8888', '4000']

    // Check if it's a private IP range
    const isPrivateIP =
      (octet1 === 192 && octet2 === 168) || // 192.168.0.0/16
      (octet1 === 10) || // 10.0.0.0/8
      (octet1 === 172 && octet2 >= 16 && octet2 <= 31) || // 172.16.0.0/12
      (octet1 === 127) // 127.0.0.0/8 (loopback)

    // Only block private IPs if they're explicitly on a development port
    // Empty port means standard HTTP/HTTPS (80/443) which could be production
    if (isPrivateIP && port !== '' && devPorts.includes(port)) {
      return true
    }
  }

  return false
}

/**
 * Check if localStorage has internal flag
 */
export function hasInternalLocalStorageFlag(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false

  try {
    const internalFlag = window.localStorage.getItem('posthog_internal')
    return internalFlag === 'true'
  } catch {
    // localStorage might be disabled or throw errors
    return false
  }
}

/**
 * Comprehensive check: Is this internal/test traffic?
 * Checks localStorage flag set via /tracking page.
 * Users must manually enable exclusion via /tracking page.
 */
export function isInternalTraffic(): {
  isInternal: boolean
  reason: 'localStorage' | null
} {
  // Check localStorage flags (set via /tracking page)
  if (hasInternalLocalStorageFlag()) {
    return { isInternal: true, reason: 'localStorage' }
  }

  return { isInternal: false, reason: null }
}

